import * as React from "react";
import { Button, Typography, Box, Grid, Divider, IconButton, Icon, Dialog, DialogContent, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import NumberFormat from "react-number-format";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getBalanceDetails, getCancelOTP, getTransactionHistory, selectPaymentState } from "../../resui/payments/sagas/residentpaymentsSlice";
import SpinnerModal from "./SpinnerModal";
import { format } from "date-fns";
import CancelIcon from "../../icons/cancelIcon.svg";
import PaymentConfirmation from "./PaymentConfirmation";
import { UIContextEnum, defaultUIContext } from "../../pmui/residents/MakeAdhocOtp";
import { useCheckAPPromotionStatus } from "../../resui/payments/components/UseSharedMethods";
import ExportIcon from "../../icons/exportIcon2.svg";
import { getExportTransDetails } from "../../pmui/transactions/sagas/pmTransactionsSlice";

const useStyles = makeStyles({
  modalContainer: {
    background: "#FFFFFF",
    boxShadow: "none",
    borderRadius: "24px",
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "33px",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "0 auto",
    marginTop: "10%",
    "& .MuiButton-root": {
      fontWeight: "800",
      minWidth: "100px",
    },
  },
  reviewAndSubmitMainDetails: {
    marginBottom: "18px",
  },
  apBtn: {
    border: "2px solid #39A2DB !important",
    borderRadius: "24px !important",
    padding: '6px 10px !important',
  },
  apBtnText: {
    textTransform: 'capitalize',
    fontFamily: 'Work Sans !important',
    fontWeight: '700 !important',
    fontSize: '14px !important',
    lineHeight: '18px !important',
  },
});

interface ModalProps {
  openStateThankYou: boolean;
  closeCallbackThankYou: (event: object, reason: string) => void;
  totalAmount: number;
  paymentID: string;
  transactionDate?: string;
  paidAmount: number;
  fee: number;
  pmCompany: string;
  propertyName: string;
  leaseHolderName: string;
  last4: string;
  deviceWidth: number;
  promoteToAP?: () => void;
}

export default function ThankYouForYourPaymentModal(props: ModalProps) {
  const classes = useStyles();
  const currentUI = React.useContext(defaultUIContext);
  const ApPromotionStatus = useCheckAPPromotionStatus();

  const dialogSize = {
    left: 7,
    right: 5,
  };

  const handleCloseCancelOTPSpinnerModal = React.useCallback(() => {
    setIsCancelOTPSpinnerModalOpen(false);
  }, []);
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const handleOpenConfirmation = () => setOpenConfirmation(true);

  const handleCloseConfirmation = () => setOpenConfirmation(false);
  const dispatch = useDispatch();
  const { isCancelOTPError, isCancelOTPSuccess, cancelOTPError } = useSelector(selectPaymentState);
  const [isCancelOTPSpinnerModalOpen, setIsCancelOTPSpinnerModalOpen] = React.useState(false);
  const [cancelOTPSpinnerStatus, setCancelOTPSpinnerStatus] = React.useState<string | null>(null);

  const refreshTransactionHistory = React.useCallback(() => {
    dispatch(getTransactionHistory());
  }, [dispatch]);

  React.useEffect(() => {
    if (isCancelOTPSuccess) {
      setCancelOTPSpinnerStatus("stop");
      refreshTransactionHistory();
      dispatch(getBalanceDetails());//we need to refresh the pending payments on balance title
    }

    if (isCancelOTPError) {
      handleCloseCancelOTPSpinnerModal();
    }
  }, [isCancelOTPSuccess, isCancelOTPError, cancelOTPError, handleCloseCancelOTPSpinnerModal, refreshTransactionHistory]);

  const handleCancelPaymentClick = () => {
    dispatch(getCancelOTP({ transactionUuid: props.paymentID }));
    setIsCancelOTPSpinnerModalOpen(true);
  };
  const fullScreen = useMediaQuery("(max-width:600px)");

  const handleCloseModal = () => {
    currentUI === UIContextEnum.resUI && refreshTransactionHistory();
    props.closeCallbackThankYou({}, "closeThankYouModal");
    dispatch(getBalanceDetails());//we need to refresh the pending payments on balance title
  }

  const handlePromoteToAp = () => {
    currentUI === UIContextEnum.resUI && refreshTransactionHistory();
    props.promoteToAP && props.promoteToAP();
    dispatch(getBalanceDetails());//we need to refresh the pending payments on balance title
  }

  return (
    <div className={classes.modalContainer}>
      <Dialog
        fullScreen={fullScreen}
        open={props.openStateThankYou}
        onClose={handleCloseModal}
        fullWidth={true}
        hideBackdrop={true}
        disableEnforceFocus
        style={{
          background: "none",
          borderRadius: "24px",
        }}
        PaperProps={{
          style: {
            pointerEvents: "auto",
            height: !fullScreen ? "700px" : "100%",
            borderRadius: "24px",
          },
        }}
      >
        <DialogContent>
          <div id="simple-modal-title">
            <Grid container>
              <Grid item xs={3.2}></Grid>
              <Grid item xs={6.8}>
                <Typography id='paymentsDetails'
                  variant="h6" component="h2">
                  Thank you for your payment
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <IconButton sx={{ ml: "30px" }} onClick={handleCloseModal}>
                  <Icon>
                    <img src={CancelIcon} alt="" width={24} height={24} />
                  </Icon>
                </IconButton>
              </Grid>
            </Grid>
          </div>

          <Typography
            style={{
              color: "#14142B",
              fontSize: "14px",
              textAlign: "center",
            }}
            sx={{ mt: 2, px: 1 }}
            id="review-submit-main-details"
          >
            Your payment is credited to your account immediately. However, it may take up to three business days to reflect on your card or bank statement.
          </Typography>

          <Typography id="payment-id" sx={{ mt: 2 }}>
            Payment ID: {props.paymentID}
          </Typography>
          <Typography id="payment-details-date">{moment(props.transactionDate).format("LLLL")}</Typography>
          <Box sx={{ width: "100%" }}>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              <Grid item xs={dialogSize.left}>
                <div>
                  <Typography
                    style={{
                      color: "#14142B",
                      fontSize: "16px",
                    }}
                    id="review-submit-main-details"
                  >
                    {" "}
                    {props.pmCompany ? `${props.pmCompany.slice(0, 20)}...` : ""}
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.right}>
                <div>
                  <Typography
                    style={{
                      color: "#14142B",
                      fontSize: "16px",
                    }}
                    id="review-submit-main-details"
                  >
                    {props.propertyName ? `${props.propertyName.slice(0, 13)}...` : ""}
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.left}>
                <div className={classes.reviewAndSubmitMainDetails}>
                  <Typography
                    style={{
                      color: "#14142B",
                      fontSize: "16px",
                    }}
                    id="review-submit-main-details"
                  >
                    {props.leaseHolderName}
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.right}>
                <div className={classes.reviewAndSubmitMainDetails}>
                  <Typography
                    style={{
                      color: "#14142B",
                      fontSize: "16px",
                    }}
                    id="review-submit-main-details"
                  >
                    {" "}
                  </Typography>
                </div>
              </Grid>
            </Grid>
            <Divider />
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ marginTop: "5px" }}>
              <Grid item xs={dialogSize.left}>
                <div>
                  <Typography id="review-submit-sub-details"> Payment Method</Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.right}>
                <div>
                  <Typography id="review-submit-sub-details">{props.last4}</Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.left}>
                <div>
                  <Typography id="review-submit-sub-details">Amount</Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.right}>
                <div>
                  <Typography id="review-submit-sub-details">
                    <NumberFormat thousandsGroupStyle="thousand" value={props.paidAmount} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} allowNegative={true} decimalScale={2} fixedDecimalScale={true} />
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.left}>
                <div>
                  <Typography id="review-submit-sub-details">Fee Amount</Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.right}>
                <div>
                  <Typography id="review-submit-sub-details">
                    <NumberFormat thousandsGroupStyle="thousand" value={props.fee} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} allowNegative={true} decimalScale={2} fixedDecimalScale={true} />
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.left}>
                <div>
                  <Typography id="review-submit-sub-details">Total Amount</Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.right}>
                <div>
                  <Typography id="review-submit-sub-details">
                    <NumberFormat thousandsGroupStyle="thousand" value={props.totalAmount} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} allowNegative={true} decimalScale={2} fixedDecimalScale={true} />
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.left}>
                <div>
                  <Typography id="review-submit-sub-details">Transaction Date</Typography>
                </div>
              </Grid>
              <Grid item xs={dialogSize.right}>
                <div>
                  <Typography id="review-submit-sub-details">{props.transactionDate ? format(new Date(props.transactionDate), "MM/dd/yy") : ""}</Typography>
                </div>
              </Grid>
              <Grid item xs={12}>
                <img
                  src={ExportIcon}
                  alt="close"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    dispatch({
                      type: getExportTransDetails.type,
                      payload: props.paymentID,
                    });
                  }}
                />
              </Grid>
            </Grid>
            {ApPromotionStatus ? (
              <div style={{ textAlign: 'right', marginTop: "10%" }}>
                <Typography
                  sx={{
                    color: "#222222",
                    fontSize: "18px",
                    fontFamily: 'Open Sans',
                    lineHeight: "34px",
                    fontWeight: "400",
                  }}
                >
                  Want to make this a recurring payment?
                </Typography>
                <div style={{ marginTop: "8px" }}>
                  <Button 
                    variant="outlined" 
                    className={classes.apBtn}
                    onClick={handleCloseModal}
                    sx={{ mr: 2.5 }}
                  >
                    <Typography className={classes.apBtnText}>No thanks</Typography>
                  </Button>
                  <Button 
                    variant="outlined" 
                    className={classes.apBtn}
                    onClick={handlePromoteToAp}
                  >
                    <Typography className={classes.apBtnText}>Make Recurring</Typography>
                  </Button>
                </div>
              </div>
            ) : (
              <div className={classes.buttonContainer}>
                <Button variant="contained" color="error" id="request-fund" onClick={handleOpenConfirmation} sx={{ backgroundColor: "#DB3939" }}>
                  Cancel Payment
                </Button>
                <Button id='btnId'
                  variant="contained"
                  onClick={handleCloseModal}
                  sx={{
                    backgroundColor: "#39A2DB",
                    borderRadius: "24px",
                  }}
                >
                  Done
                </Button>
              </div>              
            )}
          </Box>
          {isCancelOTPSpinnerModalOpen ? (
            <SpinnerModal height={fullScreen ? "100%" :"700px"} openSpinnerModal={isCancelOTPSpinnerModalOpen} handleCloseSpinnerModal={handleCloseCancelOTPSpinnerModal} spinnerStatus={cancelOTPSpinnerStatus} closeCancel={handleCloseModal} />
          ) : (
            <></>
          )}
          {openConfirmation ? <PaymentConfirmation open={openConfirmation} onClose={handleCloseConfirmation} cancelTransaction={handleCancelPaymentClick} /> : <></>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
